import { PalletTypeInterface } from 'skladon-types';
import { OrderPalletType, OrderType } from 'src/types/order';
import { TransportPackageType, TransportType } from 'src/types/transport';

const MINIMUM_PACKAGE_WEIGHT_KG = 0.01;

function buildDefaultPackages(orderBoxCount: number | undefined): TransportPackageType[] {
    const count = Math.max(1, orderBoxCount ?? 1);
    return Array.from({ length: count }, (_, index) => ({
        ID: -1 - index,
        LINE_STATE: 'E',
    }));
}

function palletDimensionCm(value: number | undefined): number {
    return value !== undefined && value >= 1 ? value : 1;
}

function minimumPackageWeight(value: number | undefined): number {
    return Math.max(value ?? 0, MINIMUM_PACKAGE_WEIGHT_KG);
}

function packageVolumeM3(lengthCm: number, widthCm: number, heightCm: number): number {
    return Number(((lengthCm * widthCm * heightCm) / 1_000_000).toFixed(6));
}

export type PalletPrefillOptions = {
    pallets?: OrderPalletType[] | null;
    palletTypes?: PalletTypeInterface[] | null;
};

function mapPalletsToPrefillPackages(
    pallets: OrderPalletType[],
    ssrcByName: Map<string, string | null>
): TransportPackageType[] {
    return pallets.map((pallet, index) => {
        const width = palletDimensionCm(pallet.WIDTH);
        const height = palletDimensionCm(pallet.HEIGHT);
        const length = palletDimensionCm(pallet.LENGTH);
        const packageType = pallet.PALLET_TYPE
            ? ssrcByName.get(pallet.PALLET_TYPE)?.trim() || null
            : null;
        return {
            ID: -1 - index,
            LINE_STATE: 'E',
            PACKAGE_TYPE: packageType,
            WIDTH: width,
            HEIGHT: height,
            LENGTH: length,
            WEIGHT: minimumPackageWeight(pallet.WEIGHT),
            VOLUME: packageVolumeM3(length, width, height),
            ID_ORDER_PALLET: pallet.ID ?? null,
        };
    });
}

function resolvePrefillPackages(
    order: OrderType,
    options?: PalletPrefillOptions
): TransportPackageType[] {
    const pallets = (options?.pallets ?? []).filter(
        (pallet) => pallet.PALLET_TYPE !== undefined || pallet.ID !== undefined
    );
    if (pallets.length === 0) {
        return buildDefaultPackages(order.ORDER_BOX_COUNT);
    }
    const ssrcByName = new Map(
        (options?.palletTypes ?? []).map((palletType) => [
            palletType.NAME,
            palletType.PACKAGE_TYPE_SSRC ?? null,
        ])
    );
    return mapPalletsToPrefillPackages(pallets, ssrcByName);
}

export const DEFAULT_SENDER_FIELDS = [
    'SENDER_NAME',
    'SENDER_ATTENTION_NAME',
    'SENDER_EMAIL',
    'SENDER_PHONE',
    'SENDER_COMPANY_NAME',
    'SENDER_STREET',
    'SENDER_CITY',
    'SENDER_STATE',
    'SENDER_POST_CODE',
] as const;

export type DefaultSenderPrefill = Pick<TransportType, (typeof DEFAULT_SENDER_FIELDS)[number]>;

function isEmptySenderField(value: string | number | null | undefined): boolean {
    return value === undefined || value === null || value === '';
}

/** Fills empty SENDER_* on the transport from warehouse defaults without overwriting user input. */
export function mergeDefaultSenderIntoTransport(
    current: TransportType,
    sender: Partial<DefaultSenderPrefill>
): TransportType {
    const next: TransportType = { ...current };
    for (const key of DEFAULT_SENDER_FIELDS) {
        const incoming = sender[key];
        if (isEmptySenderField(next[key]) && !isEmptySenderField(incoming)) {
            next[key] = incoming;
        }
    }
    return next;
}

export function parseDefaultSenderPayload(value: unknown): Partial<DefaultSenderPrefill> | null {
    if (typeof value !== 'object' || value === null) {
        return null;
    }
    const record = value as Record<string, unknown>;
    const sender: Partial<DefaultSenderPrefill> = {};
    let hasField = false;
    for (const key of DEFAULT_SENDER_FIELDS) {
        const field = record[key];
        if (typeof field === 'string') {
            sender[key] = field;
            hasField = true;
        } else if (field === null) {
            sender[key] = undefined;
        }
    }
    return hasField ? sender : null;
}

/** Prefill for atomic manual transport creation from an order detail. */
export function orderToManualTransportPrefill(
    order: OrderType,
    sender?: Partial<DefaultSenderPrefill>,
    options?: PalletPrefillOptions
): Partial<TransportType> {
    return {
        TYPE: 'expedition',
        SHIPPER: order.SHIPPER,
        SHIPPER_SERVICE_CODE: order.SHIPPER_SERVICE_CODE,
        ADDRESS_ID:
            order.ADDRESS_ID === undefined || order.ADDRESS_ID === null || order.ADDRESS_ID === ''
                ? undefined
                : Number(order.ADDRESS_ID),
        TRACKING_NUMBER: order.TRACKING_NUMBER,
        RECIPIENT_NAME: order.RECIPIENT_NAME,
        RECIPIENT_ATTENTION_NAME: order.RECIPIENT_ATTENTION_NAME,
        RECIPIENT_EMAIL: order.RECIPIENT_EMAIL,
        RECIPIENT_PHONE: order.RECIPIENT_PHONE,
        RECIPIENT_COMPANY_NAME: order.RECIPIENT_COMPANY_NAME,
        RECIPIENT_COMPANY_ID: order.RECIPIENT_COMPANY_ID,
        RECIPIENT_TAX_ID: order.RECIPIENT_TAX_ID,
        RECIPIENT_STREET: order.RECIPIENT_STREET,
        RECIPIENT_ADDRESS_ADDITIONAL_INFO: order.RECIPIENT_ADDRESS_ADDITIONAL_INFO,
        RECIPIENT_CITY: order.RECIPIENT_CITY,
        RECIPIENT_STATE: order.RECIPIENT_STATE,
        RECIPIENT_PROVINCE_CODE: order.RECIPIENT_PROVINCE_CODE,
        RECIPIENT_POST_CODE: order.RECIPIENT_POST_CODE,
        COD_PRICE: order.COD_PRICE,
        COD_CURRENCY: order.COD_CURRENCY,
        COD_REF_NUMBER: order.COD_REF_NUMBER,
        PAID: order.PAID ?? 0,
        PAYMENT_METHOD: order.PAYMENT_METHOD,
        PRICE: order.PRICE,
        CURRENCY: order.CURRENCY,
        SHIPPING_PRICE: order.SHIPPING_PRICE,
        SHIPPING_CURRENCY: order.SHIPPING_CURRENCY,
        MESSAGE: order.MESSAGE,
        INVOICE_NUMBER: order.INVOICE_NUMBER,
        ORDER_BOX_COUNT: order.ORDER_BOX_COUNT,
        TRANSPORT_PAID_BY_SKLADON: order.TRANSPORT_PAID_BY_SKLADON,
        PACKAGES: resolvePrefillPackages(order, options),
        ...(sender ?? {}),
    };
}
